/**
 * ColumnsPanel — przycisk „Kolumny" i panel (popover Radix w PORTALU) z listą WSZYSTKICH kolumn
 * tabeli w bieżącej kolejności: widoczność (checkbox), kolejność (przeciąganie myszą lub
 * klawiaturą, tylko tryb `full`), oznaczenia „wymagana" / „nowa" i wyszukiwarka. Wzorowany na
 * starym „ColumnChooser" z WF-owego web.
 *
 * Tryb `full` (układ własny): przeciąganie uchwytem w pionie (wyłączone, gdy wpisano frazę
 * wyszukiwania — kolejność na przefiltrowanej liście nie odpowiada rzeczywistej) i przycisk
 * „Przywróć domyślne" (nieaktywny, gdy w `settings` nie ma żadnego zapisanego układu). Tryb
 * `hideOnly` (układ systemowy): same checkboxy; kolumna ukryta już w układzie bazowym jest
 * odznaczona i zablokowana z podpowiedzią — tenant może tylko ukrywać. Kolumny
 * wymagane/zablokowane są zawsze widoczne i nie da się ich odznaczyć; w obu trybach nie da się też
 * odznaczyć OSTATNIEJ widocznej kolumny (checkbox zablokowany z podpowiedzią).
 *
 * Stan otwarcia trzyma edytor (`open` / `onOpenChange`): naraz otwarty jest najwyżej jeden
 * popover (panel albo menu kolumny). Esc w trakcie przeciągania klawiaturą anuluje samo
 * przeciąganie (panel zostaje), kolejny Esc zamyka panel; klik poza panelem w trakcie przeciągania
 * go nie zamyka (najpierw trzeba dokończyć lub anulować przeciąganie). Czy „trwa przeciąganie",
 * sprawdzamy tylko w treści TEGO panelu. Gdy edytor stoi w oknie (`Overlay`), treść panelu
 * rejestruje się w nim jako portal (`useOverlayPortalScopeRef`) — okno przepuszcza wtedy Esc do
 * dnd-kit i nie zamyka się kliknięciem w tło, dopóki przeciąganie w panelu trwa.
 *
 * Komponent niczego nie zapisuje — woła `onToggleVisible` / `onMove` / `onReset`, a edytor liczy
 * z tego łatkę `settings`. Kreator potrafi zrobić z płótna element nieklikalny
 * (`pointer-events: none` na przodku): przycisk i panel dostają `pointerEvents: auto`.
 * Klik i wciśnięcie myszy nie bąbelkują wyżej (opakowanie, żeby Radix widział własne zdarzenia).
 */

import { type ReactNode, type Ref, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  type EmbeddedTableColumnsWriteMode,
  isEmbeddedTableColumnPinned,
  lastVisibleEmbeddedTableColumnId,
  type ResolvedEmbeddedTableColumn,
} from '@saas/shared';
import { Columns3, Lock, Plus, RotateCcw } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { SearchInput } from '@/components/SearchInput';
import { useOverlayPortalScopeRef } from '@/hooks/useOverlayPortalScope';
import { isDragInProgress } from '@/lib/overlay-stack';
import { cn } from '@/lib/utils';

import { restrictToVerticalAxis } from './axis-modifiers';
import { GripHandle } from './GripHandle';
import { composeRefs, keepFocusIfMoved, stopBubble, useOnClosed } from './interaction-helpers';
import { useDndAccessibility } from './use-dnd-accessibility';

export interface ColumnsPanelProps {
  /** Wszystkie kolumny w bieżącej kolejności (także ukryte). */
  resolved: readonly ResolvedEmbeddedTableColumn[];
  mode: EmbeddedTableColumnsWriteMode;
  /** Czy panel jest otwarty — stan trzyma edytor (naraz otwarty najwyżej jeden popover). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onToggleVisible: (id: string, visible: boolean) => void;
  /** Przeniesienie kolumny `activeId` na miejsce kolumny `overId` (tylko tryb `full`). */
  onMove: (activeId: string, overId: string) => void;
  /** „Przywróć domyślne" (tylko tryb `full`). */
  onReset: () => void;
  /** Czy w `settings` jest cokolwiek do przywrócenia (bez tego „Przywróć domyślne" jest nieaktywne). */
  canReset: boolean;
  /** Ref przycisku „Kolumny" — edytor oddaje na niego fokus po „Ukryj kolumnę" w menu nagłówka. */
  triggerRef?: Ref<HTMLButtonElement>;
  /**
   * Ref treści panelu (element w portalu) — edytor zawęża nim szukanie trwającego przeciągania
   * do własnego korzenia i tego panelu zamiast całego dokumentu.
   */
  panelRef?: Ref<HTMLDivElement>;
  /** Prefiks `data-testid` edytora; panel dokłada `-columns-…`. */
  testIdPrefix: string;
  /**
   * Przycisk „dodaj kolumnę" w stopce panelu (np. „Dodaj pole dodatkowe") — tylko tryb `full`
   * (w trybie `hideOnly` panel sam go nie pokazuje, nawet gdy `addColumn` podano). Klik oddaje
   * fokus przyciskowi „Kolumny", zamyka panel i dopiero wtedy woła `onClick`: okno otwierane przez
   * wołającego nie może stać nad otwartym popoverem (dwa nakładane na siebie stany fokusu i Esc),
   * a fokus na przycisku „Kolumny" sprawia, że okno zapamięta go jako miejsce powrotu (przycisk
   * w zamykanym panelu zniknąłby razem z nim).
   */
  addColumn?: { label: string; onClick: () => void };
}

const VERTICAL_MODIFIERS = [restrictToVerticalAxis];

/** Fraza pasuje do własnej etykiety albo do etykiety z deskryptora (bez względu na wielkość liter). */
function matchesQuery(col: ResolvedEmbeddedTableColumn, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase('pl');
  if (needle === '') return true;
  return [col.label, col.descriptor.label].some(
    (text) => text !== undefined && text.toLocaleLowerCase('pl').includes(needle),
  );
}

interface RowShared {
  mode: EmbeddedTableColumnsWriteMode;
  onToggleVisible: (id: string, visible: boolean) => void;
  testIdPrefix: string;
  /** Id jedynej widocznej kolumny (jej nie da się odznaczyć) albo `null`. */
  lastVisibleId: string | null;
}

/** Treść wiersza: [uchwyt] checkbox z etykietą + oznaczenia. Uchwyt dostarcza wołający. */
function ColumnRowBody({
  col,
  grip,
  mode,
  onToggleVisible,
  testIdPrefix,
  lastVisibleId,
}: RowShared & { col: ResolvedEmbeddedTableColumn; grip: ReactNode }) {
  const { t } = useTranslation();
  const pinned = isEmbeddedTableColumnPinned(col.descriptor);
  // Tenant na układzie systemowym nie pokaże kolumny, którą układ bazowy ukrył.
  const hiddenInBase = mode === 'hideOnly' && !pinned && !col.baseVisible;
  // Ostatnia widoczna kolumna zostaje — tabela bez kolumn nie pokazałaby wierszy.
  const lastVisible = !pinned && col.id === lastVisibleId;
  // Zablokowane pole nie dostaje fokusu — powód blokady czytnik ekranu bierze z opisu pola.
  const lastVisibleHintId = useId();

  return (
    <div className="flex items-center gap-2 px-1 py-1">
      {grip}
      <Checkbox
        containerClassName="min-w-0 flex-1"
        label={col.label ?? col.descriptor.label}
        checked={col.visible}
        disabled={pinned || hiddenInBase || lastVisible}
        onChange={(event) => onToggleVisible(col.id, event.target.checked)}
        aria-describedby={lastVisible ? lastVisibleHintId : undefined}
        data-testid={`${testIdPrefix}-columns-check-${col.id}`}
      />
      {pinned && (
        <Badge size="sm" tone="warning" icon={Lock}>
          {t('tenant.embeddedTable.requiredBadge')}
        </Badge>
      )}
      {col.isNew && (
        <Badge size="sm" tone="accent">
          {t('tenant.embeddedTable.newBadge')}
        </Badge>
      )}
      {col.descriptor.editorNote && (
        <Badge size="sm" tone="neutral" data-testid={`${testIdPrefix}-columns-note-${col.id}`}>
          {col.descriptor.editorNote}
        </Badge>
      )}
      {hiddenInBase && (
        <span className="shrink-0 text-xs text-text-muted">
          {t('tenant.embeddedTable.hiddenInBase')}
        </span>
      )}
      {lastVisible && (
        <span
          id={lastVisibleHintId}
          className="shrink-0 text-xs text-text-muted"
          data-testid={`${testIdPrefix}-columns-last-visible-${col.id}`}
        >
          {t('tenant.embeddedTable.lastVisibleHint')}
        </span>
      )}
    </div>
  );
}

function PlainColumnRow({ col, ...shared }: RowShared & { col: ResolvedEmbeddedTableColumn }) {
  return (
    <div data-testid={`${shared.testIdPrefix}-columns-row-${col.id}`}>
      <ColumnRowBody col={col} grip={null} {...shared} />
    </div>
  );
}

function SortableColumnRow({
  col,
  dragDisabled,
  ...shared
}: RowShared & { col: ResolvedEmbeddedTableColumn; dragDisabled: boolean }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.id,
    disabled: dragDisabled,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && 'relative z-10 rounded-md bg-bg-elevated opacity-80 shadow')}
      data-testid={`${shared.testIdPrefix}-columns-row-${col.id}`}
    >
      <ColumnRowBody
        col={col}
        grip={
          <GripHandle
            ariaLabel={t('tenant.embeddedTable.dragColumn', {
              name: col.label ?? col.descriptor.label,
            })}
            attributes={dragDisabled ? undefined : attributes}
            listeners={dragDisabled ? undefined : listeners}
            disabled={dragDisabled}
            data-testid={`${shared.testIdPrefix}-columns-drag-${col.id}`}
          />
        }
        {...shared}
      />
    </div>
  );
}

export function ColumnsPanel({
  resolved,
  mode,
  open,
  onOpenChange,
  onToggleVisible,
  onMove,
  onReset,
  canReset,
  triggerRef,
  panelRef,
  testIdPrefix,
  addColumn,
}: ColumnsPanelProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  // Treść panelu jest w portalu: własny ref (zakres szukania przeciągania w tym panelu), ref
  // edytora i rejestracja w otaczającym oknie (Esc / klik w tło okna z uchwytem w panelu).
  const contentRef = useRef<HTMLDivElement | null>(null);
  // Własny ref przycisku „Kolumny" (obok `triggerRef` edytora) — `triggerRef` to dowolny `Ref`, z
  // którego nie da się odczytać elementu; potrzebny, by oddać fokus przed zamknięciem panelu.
  const ownTriggerRef = useRef<HTMLButtonElement | null>(null);
  const composedTriggerRef = useMemo(
    () => composeRefs<HTMLButtonElement>(ownTriggerRef, triggerRef),
    [triggerRef],
  );
  const portalScopeRef = useOverlayPortalScopeRef<HTMLDivElement>();
  const composedContentRef = useMemo(
    () => composeRefs<HTMLDivElement>(contentRef, portalScopeRef, panelRef),
    [portalScopeRef, panelRef],
  );
  /** Czy w TYM panelu trwa przeciąganie (uchwyt w innym miejscu strony nie ma znaczenia). */
  const isPanelDragInProgress = (): boolean =>
    contentRef.current !== null && isDragInProgress(contentRef.current);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const canReorder = mode === 'full';
  // „Dodaj kolumnę" tylko w trybie `full` — decyzja panelu, nie wołającego (układ systemowy nie
  // pozwala tenantowi dokładać kolumn).
  const addColumnAction = mode === 'full' ? addColumn : undefined;
  const searchActive = query.trim() !== '';
  const filtered = resolved.filter((col) => matchesQuery(col, query));
  const shared: RowShared = {
    mode,
    onToggleVisible,
    testIdPrefix,
    lastVisibleId: lastVisibleEmbeddedTableColumnId(resolved),
  };

  const dndAccessibility = useDndAccessibility(
    filtered.map((col) => ({ id: col.id, name: col.label ?? col.descriptor.label })),
  );

  // Fraza wyszukiwania nie przechodzi na następne otwarcie panelu — także gdy panel zamknął
  // edytor (otwarto menu kolumny, zaczęto zmianę szerokości).
  useOnClosed(open, () => setQuery(''));

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onMove(String(active.id), String(over.id));
  };

  return (
    <span
      className="inline-flex shrink-0"
      style={{ pointerEvents: 'auto' }}
      onClick={stopBubble}
      onPointerDown={stopBubble}
    >
      <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <PopoverPrimitive.Trigger asChild>
          <Button
            ref={composedTriggerRef}
            variant="secondary"
            size="sm"
            leftIcon={<Columns3 className="h-4 w-4" aria-hidden="true" />}
            data-testid={`${testIdPrefix}-columns-trigger`}
          >
            {t('tenant.embeddedTable.columnsButton')}
          </Button>
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            ref={composedContentRef}
            align="end"
            sideOffset={4}
            aria-label={t('tenant.embeddedTable.columnsPanelLabel')}
            style={{ pointerEvents: 'auto' }}
            // Trwające przeciąganie klawiaturą: Esc należy do dnd-kit (anuluje przeciąganie).
            onEscapeKeyDown={(event) => {
              if (isPanelDragInProgress()) event.preventDefault();
            }}
            // Klik/fokus poza panelem w trakcie przeciągania klawiaturą nie zamyka panelu:
            // odmontowanie uchwytu zostawiłoby czujnik dnd-kit aktywny na `document`.
            onInteractOutside={(event) => {
              if (isPanelDragInProgress()) event.preventDefault();
            }}
            onCloseAutoFocus={keepFocusIfMoved}
            className="z-[10001] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2 rounded-lg border border-border-primary bg-bg-elevated p-3 shadow-lg focus:outline-none"
            data-testid={`${testIdPrefix}-columns-panel`}
          >
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder={t('tenant.embeddedTable.searchPlaceholder')}
              aria-label={t('tenant.embeddedTable.searchLabel')}
              data-testid={`${testIdPrefix}-columns-search`}
            />

            <div className="max-h-72 overflow-y-auto" data-testid={`${testIdPrefix}-columns-list`}>
              {resolved.length === 0 ? (
                <p className="px-1 py-2 text-sm text-text-muted">
                  {t('tenant.embeddedTable.noColumns')}
                </p>
              ) : filtered.length === 0 ? (
                <p className="px-1 py-2 text-sm text-text-muted">
                  {t('tenant.embeddedTable.noMatches')}
                </p>
              ) : canReorder ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={VERTICAL_MODIFIERS}
                  accessibility={dndAccessibility}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={filtered.map((col) => col.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filtered.map((col) => (
                      <SortableColumnRow
                        key={col.id}
                        col={col}
                        dragDisabled={searchActive}
                        {...shared}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                filtered.map((col) => <PlainColumnRow key={col.id} col={col} {...shared} />)
              )}
            </div>

            {/* `addColumnAction` istnieje tylko w trybie `full` (= `canReorder`), więc sama
                `canReorder` wystarcza za warunek stopki. */}
            {canReorder && (
              <div className="flex items-center justify-between gap-2 border-t border-border-secondary pt-2">
                {addColumnAction !== undefined && (
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => {
                      // Fokus na „Kolumny" PRZED zamknięciem panelu: okno otwierane przez wołającego
                      // zapamiętuje element z fokusem jako cel powrotu — przycisk z panelu zniknie.
                      ownTriggerRef.current?.focus();
                      onOpenChange(false);
                      addColumnAction.onClick();
                    }}
                    data-testid={`${testIdPrefix}-columns-add`}
                  >
                    {addColumnAction.label}
                  </Button>
                )}
                <Button
                  className="ml-auto"
                  variant="ghost"
                  size="sm"
                  leftIcon={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                  disabled={!canReset}
                  onClick={onReset}
                  data-testid={`${testIdPrefix}-columns-reset`}
                >
                  {t('tenant.embeddedTable.resetDefaults')}
                </Button>
              </div>
            )}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </span>
  );
}
